/**
 * `/api/characters/{id}/{symbols,gear-stars,hexa,goals,snapshots}` — the WP7 per-character
 * progression surface. See `GoalsDtos` / `GoalsController` / `GoalsService` for the authoritative
 * shapes.
 *
 * Symbols/gear-stars/HEXA are steppers a player taps repeatedly, so those three get the same
 * optimistic-apply + rollback-on-error shape as WP6's `useUpsertBossPlan`. Goals/snapshots are
 * modal-driven CRUD (create/edit/delete through a form), so — like `Characters.tsx`'s account and
 * character mutations — they invalidate on success rather than hand-rolling an optimistic merge for
 * an interaction that isn't latency-sensitive.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';

// -------------------------------------------------------------------------------------------
// Symbols
// -------------------------------------------------------------------------------------------

export interface SymbolProgressDto {
  symbolSlug: string;
  symbolName: string;
  symbolType: 'ARCANE' | 'SACRED' | 'GRAND_SACRED';
  region: string;
  levelReq: number;
  maxLevel: number;
  level: number;
  symbolsHeld: number;
  verified: boolean;
}

export function symbolsQueryKey(characterId: number) {
  return ['character-symbols', characterId] as const;
}

export function useCharacterSymbols(characterId: number | null) {
  return useQuery({
    queryKey: symbolsQueryKey(characterId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<SymbolProgressDto[]>(`/api/characters/${characterId}/symbols`, signal),
    enabled: characterId !== null,
  });
}

export function useUpsertSymbol(characterId: number) {
  const queryClient = useQueryClient();
  const key = symbolsQueryKey(characterId);
  return useMutation({
    mutationFn: (vars: { symbolSlug: string; level: number; symbolsHeld: number }) =>
      apiSend<SymbolProgressDto>('PUT', `/api/characters/${characterId}/symbols/${vars.symbolSlug}`, {
        level: vars.level,
        symbolsHeld: vars.symbolsHeld,
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SymbolProgressDto[]>(key);
      queryClient.setQueryData<SymbolProgressDto[]>(key, (old) =>
        old?.map((s) =>
          s.symbolSlug === vars.symbolSlug ? { ...s, level: vars.level, symbolsHeld: vars.symbolsHeld } : s,
        ),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that symbol.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

// -------------------------------------------------------------------------------------------
// Gear stars
// -------------------------------------------------------------------------------------------

export interface GearStarDto {
  slotKey: string;
  stars: number;
}

export function gearStarsQueryKey(characterId: number) {
  return ['character-gear-stars', characterId] as const;
}

export function useCharacterGearStars(characterId: number | null) {
  return useQuery({
    queryKey: gearStarsQueryKey(characterId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<GearStarDto[]>(`/api/characters/${characterId}/gear-stars`, signal),
    enabled: characterId !== null,
  });
}

export function useUpsertGearStar(characterId: number) {
  const queryClient = useQueryClient();
  const key = gearStarsQueryKey(characterId);
  return useMutation({
    mutationFn: (vars: { slotKey: string; stars: number }) =>
      apiSend<GearStarDto>('PUT', `/api/characters/${characterId}/gear-stars/${vars.slotKey}`, {
        stars: vars.stars,
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GearStarDto[]>(key);
      queryClient.setQueryData<GearStarDto[]>(key, (old) =>
        old?.map((s) => (s.slotKey === vars.slotKey ? { ...s, stars: vars.stars } : s)),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that slot.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

// -------------------------------------------------------------------------------------------
// HEXA (REV9: level-based per-node-instance tracking — the server computes used/missing SE and
// fragments from the researched per-level curve, so the client never hardcodes curve numbers)
// -------------------------------------------------------------------------------------------

/**
 * REV21 (docs/specs/REV21-hexa-skill-icons.md): `skillSlug`/`skillName` resolve the node's real
 * in-game skill (from `hexa_class_skill`) — both null when unresolved (a fixed type or Common
 * Boost with no matching catalog row, or a Common Boost node the player hasn't picked yet).
 * `common_core` never resolves here (stays the existing hardcoded Sol Janus/Sol Hecate labeling).
 *
 * REV22 Fix 3 (docs/specs/REV22-hexa-stat-and-merged-masteries.md): `skills` is every catalog skill
 * for this node's slot, in wiki-listing order — a Mastery Core upgrades ALL of them at once, so
 * `skillSlug`/`skillName` always mirror `skills[0]` (the primary skill) once resolved.
 */
export interface HexaNodeSkill {
  skillSlug: string;
  skillName: string;
}

export interface HexaNodeDto {
  nodeType: string;
  nodeIndex: number;
  level: number;
  /** The player's own goal for this node (0-30, defaults to 30 — "max it" — when unset). */
  targetLevel: number;
  usedSolErda: number;
  usedFragments: number;
  /** SE/fragments needed to reach `targetLevel`, not necessarily level 30. */
  totalSolErda: number;
  totalFragments: number;
  /** `level >= targetLevel` — "goal reached", not necessarily level 30. */
  maxed: boolean;
  verified: boolean;
  skillSlug: string | null;
  skillName: string | null;
  skills: HexaNodeSkill[];
}

/**
 * REV22 Fix 2 (docs/specs/REV22-hexa-stat-and-merged-masteries.md) — the separate HEXA Stat matrix:
 * 3 fixed nodes (I/II/III). REV22 Fix 2c (same spec, added 2026-08-29) dropped the original 3-stat-
 * line tracking (6-stat enum, 0-10 per line) — a node is now just a stored `level` (0-20) and a
 * goal, exactly like every other HEXA node type (`HexaNodeDto`).
 *
 * REV22 Fix 2b (docs/research/09-hexa-level-costs.md's Addendum): unlock is a one-time exact
 * SE+fragment cost (`unlockSolErda`/`unlockFragments`, `unlocked` a boolean proxy — `level > 0` —
 * since the unlock action itself isn't separately tracked); leveling is fragment-RNG, so
 * `estFragmentsToGoal`/`estFragmentsSpent` are expected-value estimates from the backend's
 * precomputed roll-cost curve (`HexaStatCostModel`, reformulated for the level-only state by Fix
 * 2c), not exact figures — folded into `hexaRollup` marked `estimated: true`.
 */
export interface HexaStatNodeDto {
  nodeIndex: number;
  unlockLevel: number;
  level: number;
  /** The player's own goal (0-20, defaults to 20 — "max it" — when unset). */
  targetLevel: number;
  maxed: boolean;
  /** Exact one-time unlock cost for this node index (research 09 Addendum). */
  unlockSolErda: number;
  unlockFragments: number;
  /** Boolean proxy for "the unlock has happened" — `level > 0`. */
  unlocked: boolean;
  /** Expected fragments (rounded) remaining to reach `targetLevel` from the current level. */
  estFragmentsToGoal: number;
  /** Expected fragments (rounded) already spent reaching the current level, relative to the same
   *  `targetLevel` trajectory (an estimate — the actual RNG path is unknowable). */
  estFragmentsSpent: number;
}

/** The per-character `GET /hexa` response shape — the node list plus the sibling HEXA Stat matrix. */
export interface HexaNodesResponse {
  nodes: HexaNodeDto[];
  statNodes: HexaStatNodeDto[];
}

export function hexaQueryKey(characterId: number) {
  return ['character-hexa', characterId] as const;
}

export function useCharacterHexa(characterId: number | null) {
  return useQuery({
    queryKey: hexaQueryKey(characterId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<HexaNodesResponse>(`/api/characters/${characterId}/hexa`, signal),
    enabled: characterId !== null,
  });
}

/**
 * Upserts a single node's level. Not optimistic: the response's used/missing figures come from the
 * server-side curve lookup, so there's nothing correct to render before the round-trip completes
 * (unlike symbols/gear-stars, where the edited field is exactly what's rendered).
 */
export function useUpsertHexaNode(characterId: number) {
  const invalidate = useInvalidateHexa(characterId);
  return useMutation({
    mutationFn: (vars: {
      nodeType: string;
      nodeIndex: number;
      level: number;
      targetLevel?: number;
      skillSlug?: string;
    }) =>
      apiSend<HexaNodeDto>('PUT', `/api/characters/${characterId}/hexa/${vars.nodeType}/${vars.nodeIndex}`, {
        level: vars.level,
        targetLevel: vars.targetLevel ?? null,
        skillSlug: vars.skillSlug ?? null,
      }),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that node.';
      pushToast(message);
    },
  });
}

export function useDeleteHexaNode(characterId: number) {
  const invalidate = useInvalidateHexa(characterId);
  return useMutation({
    mutationFn: (vars: { nodeType: string; nodeIndex: number }) =>
      apiSend<void>('DELETE', `/api/characters/${characterId}/hexa/${vars.nodeType}/${vars.nodeIndex}`),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not remove that node.';
      pushToast(message);
    },
  });
}

function useInvalidateHexa(characterId: number) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: hexaQueryKey(characterId) });
}

// -------------------------------------------------------------------------------------------
// HEXA Stat (REV22 Fix 2/2c — docs/specs/REV22-hexa-stat-and-merged-masteries.md)
// -------------------------------------------------------------------------------------------

/** REV22 Fix 2c collapsed HEXA Stat to level+goal — this now mirrors `useUpsertHexaNode` exactly,
 *  including the "omitted `targetLevel` leaves the stored goal untouched" convention. */
export function useUpsertHexaStatNode(characterId: number) {
  const invalidate = useInvalidateHexa(characterId);
  return useMutation({
    mutationFn: (vars: { nodeIndex: number; level: number; targetLevel?: number }) =>
      apiSend<HexaStatNodeDto>('PUT', `/api/characters/${characterId}/hexa-stat/${vars.nodeIndex}`, {
        level: vars.level,
        targetLevel: vars.targetLevel ?? null,
      }),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that HEXA Stat node.';
      pushToast(message);
    },
  });
}

export function useDeleteHexaStatNode(characterId: number) {
  const invalidate = useInvalidateHexa(characterId);
  return useMutation({
    mutationFn: (nodeIndex: number) => apiSend<void>('DELETE', `/api/characters/${characterId}/hexa-stat/${nodeIndex}`),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not remove that HEXA Stat node.';
      pushToast(message);
    },
  });
}

// -------------------------------------------------------------------------------------------
// Goals
// -------------------------------------------------------------------------------------------

export interface GoalDto {
  id: number;
  characterId: number;
  type: string;
  title: string;
  target: unknown;
  progress: unknown;
  done: boolean;
  sortOrder: number;
}

export interface CreateGoalInput {
  type: string;
  title: string;
  target?: unknown;
  progress?: unknown;
}

export type UpdateGoalInput = Partial<CreateGoalInput> & { done?: boolean };

export function goalsQueryKey(characterId: number) {
  return ['character-goals', characterId] as const;
}

export function useCharacterGoals(characterId: number | null) {
  return useQuery({
    queryKey: goalsQueryKey(characterId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<GoalDto[]>(`/api/characters/${characterId}/goals`, signal),
    enabled: characterId !== null,
  });
}

function useInvalidateGoals(characterId: number) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: goalsQueryKey(characterId) });
}

export function useCreateGoal(characterId: number) {
  const invalidate = useInvalidateGoals(characterId);
  return useMutation({
    mutationFn: (input: CreateGoalInput) => apiSend<GoalDto>('POST', `/api/characters/${characterId}/goals`, input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateGoal(characterId: number) {
  const invalidate = useInvalidateGoals(characterId);
  return useMutation({
    mutationFn: ({ goalId, input }: { goalId: number; input: UpdateGoalInput }) =>
      apiSend<GoalDto>('PATCH', `/api/characters/${characterId}/goals/${goalId}`, input),
    onSuccess: () => invalidate(),
  });
}

/** The "done" checkbox specifically gets optimistic apply — it's a tap-and-go toggle, not a form. */
export function useToggleGoalDone(characterId: number) {
  const queryClient = useQueryClient();
  const key = goalsQueryKey(characterId);
  return useMutation({
    mutationFn: (vars: { goalId: number; done: boolean }) =>
      apiSend<GoalDto>('PATCH', `/api/characters/${characterId}/goals/${vars.goalId}`, { done: vars.done }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GoalDto[]>(key);
      queryClient.setQueryData<GoalDto[]>(key, (old) =>
        old?.map((g) => (g.id === vars.goalId ? { ...g, done: vars.done } : g)),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not update that goal.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteGoal(characterId: number) {
  const invalidate = useInvalidateGoals(characterId);
  return useMutation({
    mutationFn: (goalId: number) => apiSend<void>('DELETE', `/api/characters/${characterId}/goals/${goalId}`),
    onSuccess: () => invalidate(),
  });
}

export function useReorderGoals(characterId: number) {
  const invalidate = useInvalidateGoals(characterId);
  return useMutation({
    mutationFn: (goalIds: number[]) =>
      apiSend<GoalDto[]>('PATCH', `/api/characters/${characterId}/goals/order`, { goalIds }),
    onSuccess: () => invalidate(),
  });
}

// -------------------------------------------------------------------------------------------
// Level snapshots
// -------------------------------------------------------------------------------------------

export interface LevelSnapshotDto {
  id: number;
  characterId: number;
  level: number;
  expPercent: number | null;
  takenAt: string;
}

export interface CreateSnapshotInput {
  level: number;
  expPercent?: number | null;
}

export function snapshotsQueryKey(characterId: number) {
  return ['character-snapshots', characterId] as const;
}

export function useCharacterSnapshots(characterId: number | null) {
  return useQuery({
    queryKey: snapshotsQueryKey(characterId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<LevelSnapshotDto[]>(`/api/characters/${characterId}/snapshots`, signal),
    enabled: characterId !== null,
  });
}

function useInvalidateSnapshots(characterId: number) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: snapshotsQueryKey(characterId) });
}

export function useCreateSnapshot(characterId: number) {
  const invalidate = useInvalidateSnapshots(characterId);
  return useMutation({
    mutationFn: (input: CreateSnapshotInput) =>
      apiSend<LevelSnapshotDto>('POST', `/api/characters/${characterId}/snapshots`, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteSnapshot(characterId: number) {
  const invalidate = useInvalidateSnapshots(characterId);
  return useMutation({
    mutationFn: (snapshotId: number) =>
      apiSend<void>('DELETE', `/api/characters/${characterId}/snapshots/${snapshotId}`),
    onSuccess: () => invalidate(),
  });
}
